<template>
  <div class="cf-plan-container">
    <!-- ====== 顶部：筛选区域 ====== -->
    <div class="filter-section">
      <el-form :inline="true" size="small" class="filter-form" :model="filterForm">
        <el-form-item label="房屋地址">
          <el-input
            v-model="filterForm.房屋地址"
            placeholder="请输入房屋地址"
            clearable
            style="width:200px"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>

        <el-form-item label="租客姓名">
          <el-input
            v-model="filterForm.租客姓名"
            placeholder="请输入租客姓名"
            clearable
            style="width:150px"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>

        <el-form-item label="状态">
          <el-select
            v-model="filterForm.状态"
            placeholder="请选择状态"
            multiple
            collapse-tags
            clearable
            style="width:220px"
            @change="handleSearch"
          >
            <el-option label="待收" value="待收" />
            <el-option label="部分收款" value="部分收款" />
            <el-option label="已完成" value="已完成" />
            <el-option label="已中止" value="已中止" />
          </el-select>
        </el-form-item>

        <el-form-item label="出房人">
          <el-input
            v-model="filterForm.出房人"
            placeholder="请输入出房人"
            clearable
            style="width:150px"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>

        <el-form-item label="预计收款时间">
          <el-date-picker
            v-model="filterForm.预计收款时间范围"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="timestamp"
            style="width:240px"
            @change="handleSearch"
          />
        </el-form-item>

        <el-form-item label="考核时间">
          <el-date-picker
            v-model="filterForm.考核时间范围"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="timestamp"
            style="width:240px"
            @change="handleSearch"
          />
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- ====== 中间：按钮区域 ====== -->
    <div class="button-section">
      <el-button
        type="info"
        size="medium"
        icon="el-icon-view"
        :disabled="!canOperate"
        @click="openDetailDialog"
      >
        详情
      </el-button>
      <el-button
        type="primary"
        size="medium"
        icon="el-icon-money"
        :disabled="!canOperate"
        @click="openPaymentDialog"
      >
        付款
      </el-button>
      <el-button
        type="warning"
        size="medium"
        icon="el-icon-edit"
        :disabled="!canOperate"
        @click="openStatusDialog"
      >
        修改状态
      </el-button>
      <el-button
        type="success"
        size="medium"
        icon="el-icon-document"
        :disabled="!canOperate"
        @click="openRecordDialog"
      >
        查询付款记录
      </el-button>
    </div>

    <!-- ====== 下方：表格区域 ====== -->
    <div class="table-section">
      <el-table
        ref="tableRef"
        :data="tableData"
        border
        stripe
        highlight-current-row
        v-loading="loading"
        @selection-change="handleSelectionChange"
        @row-click="handleRowClick"
        @row-dblclick="handleRowDblclick"
        style="width: 100%"
        max-height="550"
      >
        <el-table-column type="selection" width="50" align="center" />
        <el-table-column type="index" label="序号" width="60" align="center" />
        <!-- 修改1：房屋地址缩短 -->
        <el-table-column prop="房屋地址" label="房屋地址"  align="center" show-overflow-tooltip />
        <!-- 新增：付款记录条数列 -->
        <el-table-column prop="付款记录条数" label="付款记录条数" width="120" align="center" />
        <el-table-column prop="租客姓名" label="租客姓名" width="100" align="center" />
        <el-table-column prop="状态" label="状态" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="getPlanStatusType(row.状态)" size="small">
              {{ row.状态 || '-' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="出房人" label="出房人" width="100" align="center" />
        <el-table-column prop="付款时间" label="预计收款时间" width="120" align="center">
          <template slot-scope="{ row }">
            {{ formatTimestamp(row.付款时间) }}
          </template>
        </el-table-column>
        <el-table-column prop="付款金额" label="预计收款金额" width="130" align="center">
          <template slot-scope="{ row }">
            {{ row.付款金额 ? `¥${row.付款金额}` : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="已收" label="已收" width="120" align="center">
          <template slot-scope="{ row }">
            {{ row.已收 !== null && row.已收 !== undefined ? `¥${row.已收}` : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="未收" label="未收" width="120" align="center">
          <template slot-scope="{ row }">
            {{ row.未收 !== null && row.未收 !== undefined ? `¥${row.未收}` : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="考核时间" label="考核时间" width="120" align="center">
          <template slot-scope="{ row }">
            {{ formatTimestamp(row.考核时间) }}
          </template>
        </el-table-column>
        <el-table-column prop="第几次付款" label="第几次付款" width="110" align="center" />
      </el-table>

      <!-- 分页 -->
      <div class="pagination-section">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pageInfo.pageIndex"
          :page-sizes="[10, 20, 30, 50]"
          :page-size="pageInfo.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pageInfo.total"
          background
        />
      </div>
    </div>

    <!-- ====== 详情弹窗 ====== -->
    <el-dialog
      title="收款详情"
      :visible.sync="detailDialog.visible"
      width="1000px"
      :close-on-click-modal="false"
      @close="detailDialog.visible = false"
      class="detail-dialog"
      top="3vh"
    >
      <el-tabs v-model="detailDialog.activeTab" type="border-card" v-loading="detailDialog.loading">
        <!-- Tab 1: 收款信息 -->
        <el-tab-pane label="收款信息" name="info">
          <el-descriptions :column="2" border size="medium">
            <el-descriptions-item label="房屋地址" :span="2">
              {{ detailDialog.data.房屋地址 || '-' }}
            </el-descriptions-item>

            <el-descriptions-item label="租客姓名">
              {{ detailDialog.data.租客姓名 || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="第几次付款">
              {{ detailDialog.data.第几次付款 || '-' }}
            </el-descriptions-item>

            <el-descriptions-item label="预计付款时间">
              {{ formatTimestamp(detailDialog.data.付款时间) }}
            </el-descriptions-item>
            <el-descriptions-item label="预计付款金额">
              {{ detailDialog.data.付款金额 ? `¥${detailDialog.data.付款金额}` : '-' }}
            </el-descriptions-item>

            <el-descriptions-item label="已收">
              {{ detailDialog.data.已收 !== null && detailDialog.data.已收 !== undefined ? `¥${detailDialog.data.已收}` : '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="未收">
              {{ detailDialog.data.未收 !== null && detailDialog.data.未收 !== undefined ? `¥${detailDialog.data.未收}` : '-' }}
            </el-descriptions-item>

            <el-descriptions-item label="应收租金">
              {{ detailDialog.data.应收租金 ? `¥${detailDialog.data.应收租金}` : '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="应收宽带/物业费">
              {{ detailDialog.data['应收宽带/物业费'] ? `¥${detailDialog.data['应收宽带/物业费']}` : '-' }}
            </el-descriptions-item>
          </el-descriptions>
        </el-tab-pane>

        <!-- Tab 2: 付款记录（修复查询条件） -->
        <el-tab-pane label="付款记录" name="records">
          <el-table :data="detailDialog.recordList" border stripe size="small">
            <el-table-column type="index" label="序号" width="60" align="center" />
            <el-table-column prop="实际收款时间" label="收款时间" width="170" align="center">
              <template slot-scope="{ row }">
                {{ formatTimestamp(row.实际收款时间) }}
              </template>
            </el-table-column>
            <el-table-column prop="实际收款金额" label="收款金额" width="130" align="center">
              <template slot-scope="{ row }">
                {{ row.实际收款金额 ? `¥${row.实际收款金额}` : '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="租金" label="租金" width="120" align="center">
              <template slot-scope="{ row }">
                {{ row.租金 ? `¥${row.租金}` : '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="水电费" label="水电费" width="120" align="center">
              <template slot-scope="{ row }">
                {{ row.水电费 ? `¥${row.水电费}` : '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="宽带费" label="物业宽带费" width="130" align="center">
              <template slot-scope="{ row }">
                {{ row.宽带费 ? `¥${row.宽带费}` : '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="押金总额" label="押金总额" width="120" align="center">
              <template slot-scope="{ row }">
                {{ row.押金总额 ? `¥${row.押金总额}` : '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="状态" label="状态" width="110" align="center">
              <template slot-scope="{ row }">
                <el-tag :type="getDetailStatusType(row.状态)" size="small">
                  {{ row.状态 || '-' }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
          <div v-if="detailDialog.recordList.length === 0" class="empty-tip">
            暂无付款记录
          </div>
        </el-tab-pane>

        <!-- Tab 3: 付款凭证 -->
        <el-tab-pane label="付款凭证" name="certificates">
          <div v-if="detailDialog.certificateList.length === 0" class="empty-tip">
            暂无付款凭证
          </div>
          <div v-else>
            <div v-for="(group, index) in detailDialog.certificateList" :key="index" class="certificate-group">
              <div class="certificate-group-title">
                明细 {{ index + 1 }} - {{ group.detailId || '' }}
              </div>
              <el-table :data="group.files" border stripe size="small">
                <el-table-column type="index" label="序号" width="60" align="center" />
                <el-table-column prop="show_name" label="文件名称" min-width="200" show-overflow-tooltip />
                <el-table-column label="预览" width="150" align="center">
                  <template slot-scope="{ row }">
                    <div v-if="isImage(row.suffix) && (row.show_path_full || row.show_path)" class="preview-thumbnail">
                      <el-image
                        :src="row.show_path_full || row.show_path"
                        fit="cover"
                        style="width: 80px; height: 60px; border-radius: 4px; cursor: pointer;"
                        :preview-src-list="[row.show_path_full || row.show_path]"
                      />
                    </div>
                    <span v-else class="preview-placeholder">仅支持图片预览</span>
                  </template>
                </el-table-column>
                <el-table-column prop="upload_time_clean" label="上传时间" width="170" align="center" />
                <el-table-column label="操作" width="160" align="center">
                  <template slot-scope="{ row }">
                    <el-button type="text" size="mini" @click="handleViewFile(row)">查看</el-button>
                    <el-button type="text" size="mini" @click="handleDownloadFile(row)">下载</el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>

      <span slot="footer">
        <el-button @click="detailDialog.visible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- ====== 付款弹窗 ====== -->
    <el-dialog
      title="付款"
      :visible.sync="paymentDialog.visible"
      width="700px"
      :close-on-click-modal="false"
      @close="resetPaymentForm"
      class="payment-dialog"
      top="3vh"
    >
      <!-- ... 付款弹窗内容不变 ... -->
      <el-form
        ref="paymentFormRef"
        :model="paymentForm"
        :rules="paymentRules"
        label-width="130px"
        size="small"
      >
        <!-- 第1行：房屋地址 -->
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="房屋地址">
              <el-input v-model="paymentForm.房屋地址" disabled />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 第2行：租客姓名 + 第几次付款 -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="租客姓名">
              <el-input v-model="paymentForm.租客姓名" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="第几次付款">
              <el-input-number
                v-model="paymentForm.第几次付款"
                disabled
                style="width:100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 第3行：预计付款时间 + 预计付款金额 -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="预计付款时间">
              <el-date-picker
                v-model="paymentForm.预计付款时间"
                type="date"
                disabled
                value-format="timestamp"
                style="width:100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="预计付款金额">
              <el-input-number
                v-model="paymentForm.预计付款金额"
                disabled
                :precision="2"
                style="width:100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 第4行：本次收款时间 + 本次收款金额 -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="本次收款时间" prop="实际收款时间" required>
              <el-date-picker
                v-model="paymentForm.实际收款时间"
                type="date"
                placeholder="请选择本次收款时间"
                value-format="timestamp"
                style="width:100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="本次收款金额" prop="实际收款金额" required>
              <el-input-number
                v-model="paymentForm.实际收款金额"
                :min="0"
                :precision="2"
                :step="100"
                controls-position="right"
                placeholder="请输入本次收款金额"
                style="width:100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 分割线 -->
        <el-row :gutter="20">
          <el-col :span="24">
            <div class="divider">
              <span class="divider-text">收款组成</span>
            </div>
          </el-col>
        </el-row>

        <!-- 第5行：租金 + 物业宽带费 -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="租金" prop="租金" required>
              <el-input-number
                v-model="paymentForm.租金"
                :min="0"
                :precision="2"
                :step="100"
                controls-position="right"
                placeholder="请输入租金"
                style="width:100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="物业宽带费" prop="宽带费" required>
              <el-input-number
                v-model="paymentForm.宽带费"
                :min="0"
                :precision="2"
                :step="100"
                controls-position="right"
                placeholder="请输入物业宽带费"
                style="width:100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 第6行：水电费 + 押金总额 -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="水电费" prop="水电费" required>
              <el-input-number
                v-model="paymentForm.水电费"
                :min="0"
                :precision="2"
                :step="100"
                controls-position="right"
                placeholder="请输入水电费"
                style="width:100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="押金总额" prop="押金总额" required>
              <el-input-number
                v-model="paymentForm.押金总额"
                :min="0"
                :precision="2"
                :step="100"
                controls-position="right"
                placeholder="请输入押金总额"
                style="width:100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 第7行：备注 -->
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="备注">
              <el-input v-model="paymentForm.备注" placeholder="请输入备注（可选）" />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 附件上传区域 -->
        <el-row :gutter="20">
          <el-col :span="24">
            <div class="file-group">
              <div class="file-group-header">
                <span class="file-group-title">付款凭证</span>
                <el-button type="primary" size="small" icon="el-icon-upload2" @click="openCertUploadDialog">
                  添加凭证
                </el-button>
              </div>
              <el-table :data="paymentDialog.certFileList" border stripe size="small">
                <el-table-column type="index" label="序号" width="60" align="center" />
                <el-table-column prop="show_name" label="文件名称" min-width="200" show-overflow-tooltip />
                <el-table-column label="预览" width="150" align="center">
                  <template slot-scope="{ row }">
                    <div v-if="isImage(row.suffix) && row.preview_url" class="preview-thumbnail">
                      <el-image
                        :src="row.preview_url"
                        fit="cover"
                        style="width: 80px; height: 60px; border-radius: 4px; cursor: pointer;"
                        :preview-src-list="[row.preview_url]"
                      />
                    </div>
                    <span v-else class="preview-placeholder">仅支持图片预览</span>
                  </template>
                </el-table-column>
                <el-table-column prop="upload_time" label="上传时间" width="170" align="center" />
                <el-table-column label="操作" width="160" align="center">
                  <template slot-scope="{ row }">
                    <el-button type="text" size="mini" @click="handleViewFile(row)">查看</el-button>
                    <el-button type="text" size="mini" style="color: #f56c6c;" @click="handleRemoveCertFile(row)">删除</el-button>
                  </template>
                </el-table-column>
              </el-table>
              <div v-if="paymentDialog.certFileList.length === 0" class="empty-tip">
                暂无付款凭证
              </div>
            </div>
          </el-col>
        </el-row>
      </el-form>

      <span slot="footer">
        <el-button @click="paymentDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="handlePaymentSubmit" :loading="paymentDialog.submitting">确认</el-button>
      </span>
    </el-dialog>

    <!-- ====== 付款凭证上传弹窗 ====== -->
    <el-dialog
      title="添加上传凭证"
      :visible.sync="certUploadDialog.visible"
      width="520px"
      :append-to-body="true"
      :close-on-click-modal="false"
      @close="resetCertUploadForm"
    >
      <el-form :model="certUploadForm" label-width="100px" size="small">
        <el-form-item label="选择文件" required>
          <el-upload
            ref="certUploadRef"
            :auto-upload="false"
            :limit="1"
            :on-change="handleCertFileChange"
            :on-remove="handleCertFileRemove"
            action="#"
            accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
          >
            <el-button size="small" type="primary">点击选择文件</el-button>
            <span slot="tip" class="el-upload__tip">支持 jpg、jpeg、png、pdf、doc、docx</span>
          </el-upload>
          <div v-if="certUploadForm.fileName" style="margin-top: 8px; font-size: 13px; color: #409EFF;">
            已选择：{{ certUploadForm.fileName }}
          </div>
        </el-form-item>

        <el-form-item label="备注">
          <el-input v-model="certUploadForm.bz" placeholder="请输入备注（可选）" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="certUploadDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="handleCertUploadSubmit" :loading="certUploadDialog.uploading">确认</el-button>
      </span>
    </el-dialog>

    <!-- ====== 修改状态弹窗 ====== -->
    <el-dialog
      title="修改状态"
      :visible.sync="statusDialog.visible"
      width="500px"
      :close-on-click-modal="false"
      @close="resetStatusForm"
      class="status-dialog"
    >
      <el-form ref="statusFormRef" :model="statusForm" label-width="120px" size="small">
        <el-form-item label="房屋地址">
          <el-input v-model="statusForm.房屋地址" disabled />
        </el-form-item>
        <el-form-item label="业务状态" prop="状态" required>
          <el-select v-model="statusForm.状态" placeholder="请选择状态" style="width:100%">
            <el-option label="待收" value="待收" />
            <el-option label="部分收款" value="部分收款" />
            <el-option label="已完成" value="已完成" />
            <el-option label="已中止" value="已中止" />
          </el-select>
        </el-form-item>
      </el-form>

      <span slot="footer">
        <el-button @click="statusDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="handleStatusSubmit" :loading="statusDialog.submitting">确认</el-button>
      </span>
    </el-dialog>

    <!-- ====== 查询付款记录弹窗 ====== -->
    <el-dialog
      title="付款记录"
      :visible.sync="recordDialog.visible"
      width="1000px"
      :close-on-click-modal="false"
      @close="resetRecordDialog"
      class="record-dialog"
      top="3vh"
    >
      <el-table :data="recordDialog.recordList" border stripe size="small" v-loading="recordDialog.loading">
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column prop="实际收款时间" label="收款时间" width="170" align="center">
          <template slot-scope="{ row }">
            {{ formatTimestamp(row.实际收款时间) }}
          </template>
        </el-table-column>
        <el-table-column prop="实际收款金额" label="收款金额" width="130" align="center">
          <template slot-scope="{ row }">
            {{ row.实际收款金额 ? `¥${row.实际收款金额}` : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="租金" label="租金" width="120" align="center">
          <template slot-scope="{ row }">
            {{ row.租金 ? `¥${row.租金}` : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="水电费" label="水电费" width="120" align="center">
          <template slot-scope="{ row }">
            {{ row.水电费 ? `¥${row.水电费}` : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="宽带费" label="物业宽带费" width="130" align="center">
          <template slot-scope="{ row }">
            {{ row.宽带费 ? `¥${row.宽带费}` : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="押金总额" label="押金总额" width="120" align="center">
          <template slot-scope="{ row }">
            {{ row.押金总额 ? `¥${row.押金总额}` : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="状态" label="状态" width="110" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="getDetailStatusType(row.状态)" size="small">
              {{ row.状态 || '-' }}
            </el-tag>
          </template>
        </el-table-column>
        <!-- 操作列 -->
        <el-table-column label="操作" width="160" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              type="text"
              size="mini"
              @click="openEditRecordDialog(row)"
            >
              修改
            </el-button>
            <el-button
              type="text"
              size="mini"
              style="color: #f56c6c;"
              @click="handleDeleteRecord(row)"
              :loading="row.deleting"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <div v-if="recordDialog.recordList.length === 0" class="empty-tip">
        暂无付款记录
      </div>

      <span slot="footer">
        <el-button @click="recordDialog.visible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- ====== 修改付款记录弹窗 ====== -->
    <el-dialog
      title="修改付款记录"
      :visible.sync="editRecordDialog.visible"
      width="600px"
      :close-on-click-modal="false"
      @close="resetEditRecordForm"
      class="edit-record-dialog"
    >
      <el-form ref="editRecordFormRef" :model="editRecordForm" label-width="130px" size="small">
        <el-form-item label="实际收款时间" prop="实际收款时间" required>
          <el-date-picker
            v-model="editRecordForm.实际收款时间"
            type="date"
            placeholder="请选择实际收款时间"
            value-format="timestamp"
            style="width:100%"
          />
        </el-form-item>
        <el-form-item label="实际收款金额" prop="实际收款金额" required>
          <el-input-number
            v-model="editRecordForm.实际收款金额"
            :min="0"
            :precision="2"
            :step="100"
            controls-position="right"
            placeholder="请输入实际收款金额"
            style="width:100%"
          />
        </el-form-item>
        <el-form-item label="租金" prop="租金" required>
          <el-input-number
            v-model="editRecordForm.租金"
            :min="0"
            :precision="2"
            :step="100"
            controls-position="right"
            placeholder="请输入租金"
            style="width:100%"
          />
        </el-form-item>
        <el-form-item label="物业宽带费" prop="宽带费" required>
          <el-input-number
            v-model="editRecordForm.宽带费"
            :min="0"
            :precision="2"
            :step="100"
            controls-position="right"
            placeholder="请输入物业宽带费"
            style="width:100%"
          />
        </el-form-item>
        <el-form-item label="水电费" prop="水电费" required>
          <el-input-number
            v-model="editRecordForm.水电费"
            :min="0"
            :precision="2"
            :step="100"
            controls-position="right"
            placeholder="请输入水电费"
            style="width:100%"
          />
        </el-form-item>
        <el-form-item label="押金总额" prop="押金总额" required>
          <el-input-number
            v-model="editRecordForm.押金总额"
            :min="0"
            :precision="2"
            :step="100"
            controls-position="right"
            placeholder="请输入押金总额"
            style="width:100%"
          />
        </el-form-item>
        <el-form-item label="状态" prop="状态" required>
          <el-select v-model="editRecordForm.状态" placeholder="请选择状态" style="width:100%">
            <el-option label="待审核" value="待审核" />
            <el-option label="已通过" value="已通过" />
            <el-option label="退回" value="退回" />
          </el-select>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="editRecordForm.备注" placeholder="请输入备注（可选）" />
        </el-form-item>
      </el-form>

      <span slot="footer">
        <el-button @click="editRecordDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="handleEditRecordSubmit" :loading="editRecordDialog.submitting">确认</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { cfPlanApi } from '@/api/付款/cfPlanApi'
import { fileMethod } from '@/api/files/fileMethod'

export default {
  name: 'CfPlan',

  data() {
    return {
      // ... 原有的 data 保持不变 ...
      filterForm: {
        房屋地址: '',
        租客姓名: '',
        状态: ['待收', '部分收款'],
        出房人: '',
        预计收款时间范围: [],
        考核时间范围: []
      },
      tableData: [],
      loading: false,
      selectedRows: [],
      pageInfo: {
        pageIndex: 1,
        pageSize: 10,
        total: 0
      },

      // 详情弹窗
      detailDialog: {
        visible: false,
        loading: false,
        activeTab: 'info',
        data: {},
        recordList: [],
        certificateList: []
      },

      // 付款弹窗
      paymentDialog: {
        visible: false,
        submitting: false,
        certFileList: [],
        rowData: null
      },
      paymentForm: {
        房屋地址: '',
        租客姓名: '',
        第几次付款: null,
        预计付款时间: null,
        预计付款金额: null,
        实际收款时间: null,
        实际收款金额: null,
        租金: 0,
        宽带费: 0,
        水电费: 0,
        押金总额: 0,
        备注: ''
      },
      paymentRules: {
        实际收款时间: [
          { required: true, message: '请选择本次收款时间', trigger: 'change' }
        ],
        实际收款金额: [
          { required: true, message: '请输入本次收款金额', trigger: 'blur' }
        ],
        租金: [
          { required: true, message: '请输入租金', trigger: 'blur' }
        ],
        宽带费: [
          { required: true, message: '请输入物业宽带费', trigger: 'blur' }
        ],
        水电费: [
          { required: true, message: '请输入水电费', trigger: 'blur' }
        ],
        押金总额: [
          { required: true, message: '请输入押金总额', trigger: 'blur' }
        ]
      },

      // 凭证上传弹窗
      certUploadDialog: {
        visible: false,
        uploading: false
      },
      certUploadForm: {
        file: null,
        fileName: '',
        bz: ''
      },

      // 修改状态弹窗
      statusDialog: {
        visible: false,
        submitting: false,
        rowData: null
      },
      statusForm: {
        房屋地址: '',
        状态: ''
      },

      // 查询付款记录弹窗
      recordDialog: {
        visible: false,
        loading: false,
        recordList: [],
        rowData: null
      },

      // 修改付款记录弹窗
      editRecordDialog: {
        visible: false,
        submitting: false,
        rowData: null
      },
      editRecordForm: {
        实际收款时间: null,
        实际收款金额: null,
        租金: 0,
        宽带费: 0,
        水电费: 0,
        押金总额: 0,
        状态: '',
        备注: ''
      },
      editRecordRules: {
        实际收款时间: [
          { required: true, message: '请选择实际收款时间', trigger: 'change' }
        ],
        实际收款金额: [
          { required: true, message: '请输入实际收款金额', trigger: 'blur' }
        ],
        租金: [
          { required: true, message: '请输入租金', trigger: 'blur' }
        ],
        宽带费: [
          { required: true, message: '请输入物业宽带费', trigger: 'blur' }
        ],
        水电费: [
          { required: true, message: '请输入水电费', trigger: 'blur' }
        ],
        押金总额: [
          { required: true, message: '请输入押金总额', trigger: 'blur' }
        ],
        状态: [
          { required: true, message: '请选择状态', trigger: 'change' }
        ]
      }
    }
  },

  computed: {
    canOperate() {
      return this.selectedRows.length === 1
    }
  },

  mounted() {
    this.init()
  },

  methods: {
    // ... 原有的方法保持不变 ...

    init() {
      this.loadData()
    },

    mapFields(data) {
      if (!data) return {}

      const fieldMap = {
        'id': 'id',
        '出房ID': '出房ID',
        '状态': '状态',
        '付款时间': '付款时间',
        '付款金额': '付款金额',
        '房屋地址': '房屋地址',
        '第几次付款': '第几次付款',
        '租客姓名': '租客姓名',
        '出房人': '出房人',
        '已收': '已收',
        '未收': '未收',
        '考核时间': '考核时间',
        '应收租金': '应收租金',
        '应收宽带/物业费': '应收宽带/物业费'
      }

      const result = {}
      for (const [backendKey, frontendKey] of Object.entries(fieldMap)) {
        if (data[backendKey] !== undefined && data[backendKey] !== null) {
          result[frontendKey] = data[backendKey]
        }
      }

      const timestampFields = ['付款时间', '考核时间']
      timestampFields.forEach(field => {
        if (result[field] !== undefined && result[field] !== null && result[field] !== '') {
          const str = String(result[field])
          if (str.length === 10) {
            result[field] = result[field] * 1000
          }
        }
      })

      // 计算付款记录条数（这里先设为0，后续在 loadData 中补充）
      result.付款记录条数 = 0

      return result
    },

    loadData() {
      this.loading = true

      const params = {
        pageIndex: this.pageInfo.pageIndex - 1,
        pageSize: this.pageInfo.pageSize
      }

      if (this.filterForm.房屋地址) {
        params.房屋地址 = this.filterForm.房屋地址
      }
      if (this.filterForm.租客姓名) {
        params.租客姓名 = this.filterForm.租客姓名
      }
      if (this.filterForm.状态 && this.filterForm.状态.length > 0) {
        params.状态 = this.filterForm.状态
      }
      if (this.filterForm.出房人) {
        params.出房人 = this.filterForm.出房人
      }
      if (this.filterForm.预计收款时间范围 && this.filterForm.预计收款时间范围.length === 2) {
        const [start, end] = this.filterForm.预计收款时间范围
        params.付款时间_start = Math.floor(start / 1000)
        params.付款时间_end = Math.floor(end / 1000)
      }
      if (this.filterForm.考核时间范围 && this.filterForm.考核时间范围.length === 2) {
        const [start, end] = this.filterForm.考核时间范围
        params.考核时间_start = Math.floor(start / 1000)
        params.考核时间_end = Math.floor(end / 1000)
      }

      console.log('查询出房付款计划参数===', params)

      cfPlanApi.selectPlan(params).then(async (res) => {
        console.log('返回数据===', res)

        if (res.code === 200) {
          const rawList = res.data?.list || []
          
          // 为每条记录查询付款记录条数
          const enrichedList = []
          for (const item of rawList) {
            const mappedItem = this.mapFields(item)
            try {
              const detailRes = await cfPlanApi.selectDetail({
                付款ID: String(item.id)
              })
              if (detailRes.code === 200) {
                mappedItem.付款记录条数 = (detailRes.data?.list || []).length
              } else {
                mappedItem.付款记录条数 = 0
              }
            } catch (err) {
              mappedItem.付款记录条数 = 0
            }
            enrichedList.push(mappedItem)
          }
          
          this.tableData = enrichedList
          if (res.data?.pageIndex !== undefined) {
            this.pageInfo.pageIndex = res.data.pageIndex + 1
          }
          if (res.data?.total !== undefined) {
            this.pageInfo.total = res.data.total
          }
        } else {
          this.$message.error(res.message || '查询失败')
        }
      }).catch((err) => {
        console.error('请求失败===', err)
        this.$message.error('加载数据失败')
      }).finally(() => {
        this.loading = false
      })
    },

    // ... handleSearch, resetSearch, handleSizeChange, handleCurrentChange, handleSelectionChange, handleRowClick 保持不变 ...

    handleSearch() {
      this.pageInfo.pageIndex = 1
      this.loadData()
    },

    resetSearch() {
      this.filterForm = {
        房屋地址: '',
        租客姓名: '',
        状态: ['待收', '部分收款'],
        出房人: '',
        预计收款时间范围: [],
        考核时间范围: []
      }
      this.handleSearch()
    },

    handleSizeChange(size) {
      this.pageInfo.pageSize = size
      this.pageInfo.pageIndex = 1
      this.loadData()
    },

    handleCurrentChange(page) {
      this.pageInfo.pageIndex = page
      this.loadData()
    },

    handleSelectionChange(selection) {
      this.selectedRows = selection
    },

    handleRowClick(row) {
      this.$refs.tableRef.toggleRowSelection(row)
    },

    // ====== 双击行打开详情 ======
    handleRowDblclick(row) {
      this.selectedRows = [row]
      this.openDetailDialog()
    },

    // ... getPlanStatusType, getDetailStatusType, formatTimestamp, isImage, getFileSuffix, handleViewFile, handleDownloadFile 保持不变 ...

    getPlanStatusType(status) {
      const map = {
        '待收': 'danger',
        '部分收款': 'warning',
        '已完成': 'success',
        '已中止': 'info'
      }
      return map[status] || 'info'
    },

    getDetailStatusType(status) {
      const map = {
        '待审核': 'warning',
        '已通过': 'success',
        '退回': 'danger'
      }
      return map[status] || 'info'
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return '-'
      let ts = timestamp
      const str = String(ts)
      if (str.length === 10) {
        ts = ts * 1000
      }
      const date = new Date(ts)
      if (isNaN(date.getTime())) {
        return '-'
      }
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    isImage(suffix) {
      if (!suffix) return false
      const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']
      return imageTypes.includes(suffix.toLowerCase())
    },

    getFileSuffix(fileName) {
      const parts = fileName.split('.')
      return parts.length > 1 ? parts[parts.length - 1] : ''
    },

    handleViewFile(row) {
      const url = row.show_path_full || row.show_path
      if (url) {
        window.open(url, '_blank')
      } else {
        this.$message.warning('文件路径不存在')
      }
    },

    async handleDownloadFile(row) {
      if (!row.show_path) {
        this.$message.warning('文件路径不存在')
        return
      }

      const loading = this.$loading({
        lock: true,
        text: '正在下载文件...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })

      try {
        const res = await fileMethod.downloadFile(row.show_path)
        const blob = new Blob([res.data])
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = row.show_name || '下载文件'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        this.$message.success('下载成功')
      } catch (error) {
        console.error('下载失败:', error)
        this.$message.error('下载失败，请重试')
      } finally {
        loading.close()
      }
    },

    // ============================================================
    // 详情弹窗（修复查询条件：使用付款ID）
    // ============================================================
    async openDetailDialog() {
         console.log('=== openDetailDialog 被调用 ===')
         console.log('cfPlanApi.selectDetail:', cfPlanApi.selectDetail)
      if (this.selectedRows.length !== 1) {
        this.$message.warning('请选择一条记录')
        return
      }

      const row = this.selectedRows[0]
      this.detailDialog.data = { ...row }
      this.detailDialog.activeTab = 'info'
      this.detailDialog.visible = true
      this.detailDialog.loading = true
      this.detailDialog.recordList = []
      this.detailDialog.certificateList = []

      try {
        // 修复：使用付款ID查询明细（行数据.id = 出房付款明细.付款ID）
        const recordRes = await cfPlanApi.selectDetail({
          付款ID: String(row.id)
        })
        console.log('查询付款记录返回:', recordRes)

        if (recordRes.code === 200) {
          const records = recordRes.data?.list || []
          this.detailDialog.recordList = records.map(item => {
            if (item.实际收款时间) {
              const str = String(item.实际收款时间)
              if (str.length === 10) {
                item.实际收款时间 = item.实际收款时间 * 1000
              }
            }
            return item
          })

          // 加载每个明细的付款凭证
          const fileBaseUrl = window.applicationConfig?.fileBaseUrl || window.location.origin + '/nngy/'
          const certList = []

          for (const record of records) {
            const fileRes = await fileMethod.listFiles('出房付款明细', String(record.id), '出房付款明细凭证', null)
            if (fileRes.code === 200) {
              const files = (fileRes.data?.list || []).map(item => {
                if (item.show_path) {
                  const isFullUrl = item.show_path.startsWith('http://') || item.show_path.startsWith('https://')
                  item.show_path_full = isFullUrl ? item.show_path : fileBaseUrl + item.show_path.replace(/^\//, '')
                }
                return item
              })
              if (files.length > 0) {
                certList.push({
                  detailId: record.id,
                  files: files
                })
              }
            }
          }
          this.detailDialog.certificateList = certList
        }
      } catch (error) {
        console.error('加载详情失败:', error)
        this.$message.error('加载详情失败，请重试')
      } finally {
        this.detailDialog.loading = false
      }
    },

    // ============================================================
    // 付款弹窗（保持不变）
    // ============================================================
    openPaymentDialog() {
      if (this.selectedRows.length !== 1) {
        this.$message.warning('请选择一条记录')
        return
      }

      const row = this.selectedRows[0]
      this.paymentDialog.rowData = row
      this.paymentDialog.certFileList = []

      this.paymentForm = {
        房屋地址: row.房屋地址 || '',
        租客姓名: row.租客姓名 || '',
        第几次付款: row.第几次付款 || null,
        预计付款时间: row.付款时间 || null,
        预计付款金额: row.付款金额 || null,
        实际收款时间: new Date().getTime(),
        实际收款金额: null,
        租金: 0,
        宽带费: 0,
        水电费: 0,
        押金总额: 0,
        备注: ''
      }

      this.paymentDialog.visible = true
      this.$nextTick(() => {
        if (this.$refs.paymentFormRef) {
          this.$refs.paymentFormRef.clearValidate()
        }
      })
    },

    resetPaymentForm() {
      this.paymentDialog.submitting = false
      this.paymentDialog.certFileList = []
      if (this.$refs.paymentFormRef) {
        this.$refs.paymentFormRef.clearValidate()
      }
    },

    // ====== 付款凭证上传 ======
    openCertUploadDialog() {
      this.certUploadDialog.visible = true
      this.certUploadForm.file = null
      this.certUploadForm.fileName = ''
      this.certUploadForm.bz = ''
      this.$nextTick(() => {
        if (this.$refs.certUploadRef) {
          this.$refs.certUploadRef.clearFiles()
        }
      })
    },

    handleCertFileChange(file) {
      this.certUploadForm.file = file.raw
      this.certUploadForm.fileName = file.name
    },

    handleCertFileRemove() {
      this.certUploadForm.file = null
      this.certUploadForm.fileName = ''
    },

    resetCertUploadForm() {
      this.certUploadForm.file = null
      this.certUploadForm.fileName = ''
      this.certUploadForm.bz = ''
      this.certUploadDialog.uploading = false
      if (this.$refs.certUploadRef) {
        this.$refs.certUploadRef.clearFiles()
      }
    },

    async handleCertUploadSubmit() {
      if (!this.certUploadForm.file) {
        this.$message.warning('请选择文件')
        return
      }

      this.certUploadDialog.uploading = true

      try {
        const res = await fileMethod.uploadOnly(this.certUploadForm.file)
        console.log('上传凭证返回:', res)

        if (res.code === 200) {
          const data = res.data
          const fileBaseUrl = window.applicationConfig?.fileBaseUrl || window.location.origin + '/nngy/'
          const showPath = data.showPath || ''
          const fullUrl = fileBaseUrl + showPath.replace(/^\//, '')

          const fileInfo = {
            save_name: data.saveName || '',
            save_path: data.savePath || '',
            show_name: data.showName || this.certUploadForm.file.name,
            show_path: data.showPath || '',
            suffix: data.suffix || this.getFileSuffix(this.certUploadForm.file.name),
            bz: this.certUploadForm.bz || '',
            upload_time: new Date().toLocaleString(),
            preview_url: fullUrl,
            show_path_full: fullUrl
          }

          this.paymentDialog.certFileList.push(fileInfo)
          this.$message.success('上传成功')
          this.certUploadDialog.visible = false
        } else {
          this.$message.error(res.message || '上传失败')
        }
      } catch (error) {
        console.error('上传失败:', error)
        this.$message.error(error.message || '上传失败，请稍后重试')
      } finally {
        this.certUploadDialog.uploading = false
      }
    },

    handleRemoveCertFile(row) {
      this.$confirm(
        `确认移除文件 "${row.show_name}" 吗？`,
        '提示',
        {
          type: 'warning',
          confirmButtonText: '确认移除',
          cancelButtonText: '取消'
        }
      ).then(() => {
        const index = this.paymentDialog.certFileList.findIndex(item => item.save_name === row.save_name)
        if (index !== -1) {
          this.paymentDialog.certFileList.splice(index, 1)
        }
        this.$message.success('已移除')
      }).catch(() => {})
    },

    // ====== 付款提交 ======
async handlePaymentSubmit() {
  try {
    await this.$refs.paymentFormRef.validate()
  } catch (error) {
    this.$message.warning('请完善表单信息')
    return
  }

  const form = this.paymentForm
  const row = this.paymentDialog.rowData

  const totalAmount = (form.租金 || 0) + (form.宽带费 || 0) + (form.水电费 || 0) + (form.押金总额 || 0)

  if (form.实际收款金额 !== totalAmount) {
    try {
      await this.$confirm(
        `本次收款金额 (${form.实际收款金额}) 与各项合计 (${totalAmount}) 不一致，是否继续？`,
        '提示',
        {
          type: 'warning',
          confirmButtonText: '继续',
          cancelButtonText: '取消'
        }
      )
    } catch {
      return
    }
  }

  try {
    await this.$confirm(
      `确认本次收款金额 ¥${form.实际收款金额} 吗？`,
      '确认付款',
      {
        type: 'warning',
        confirmButtonText: '确认',
        cancelButtonText: '取消'
      }
    )
  } catch {
    return
  }

  this.paymentDialog.submitting = true

  try {
    // ====== 组装 fileList ======
    const fileList = this.paymentDialog.certFileList.map(item => ({
      save_name: item.save_name,
      save_path: item.save_path,
      show_name: item.show_name,
      show_path: item.show_path,
      suffix: item.suffix,
      type_2: item.bz || ''  // 备注放入 type_2
    }))

    // ====== 组装插入参数 ======
    const detailParams = {
      出房ID: String(row.出房ID || ''),
      付款ID: String(row.id),
      房屋地址: form.房屋地址,
      付款金额: form.实际收款金额,
      租金: form.租金 || 0,
      水电费: form.水电费 || 0,
      宽带费: form.宽带费 || 0,
      押金总额: form.押金总额 || 0,
      实际收款金额: form.实际收款金额,
      租客姓名: form.租客姓名,
      出房人: row.出房人 || '',
      第几次付款: form.第几次付款 || 1,
      // 可选字段
      实际收款时间: Math.floor(form.实际收款时间 / 1000),
      备注: form.备注 || '',
      出房门店: row.出房门店 || '',
      合同起始日: row.合同起始日 ? row.合同起始日  : null,
      合同到期日: row.合同到期日 ? row.合同起始日  : null,
       付款时间: row.付款时间 ? Math.floor(row.付款时间 / 1000) : null,
      fileList: fileList  // 附件列表直接传入
    }

    console.log('插入出房付款明细参数===', detailParams)

    // ====== 调用 /detail/insert 接口 ======
    const detailRes = await cfPlanApi.insertDetail(detailParams)
    console.log('插入出房付款明细返回===', detailRes)

    if (detailRes.code !== 200) {
      this.$message.error(detailRes.message || '插入付款明细失败')
      this.paymentDialog.submitting = false
      return
    }

    // 更新出房付款计划
    const updateRes = await cfPlanApi.updatePlanByDetails({
      id: String(row.id)
    })
    console.log('更新出房付款计划返回===', updateRes)

    if (updateRes.code !== 200) {
      this.$message.warning('明细已插入，但计划汇总更新失败，请手动刷新')
    } else {
      this.$message.success('付款成功！')
    }

    this.paymentDialog.visible = false
    this.loadData()

  } catch (error) {
    console.error('付款失败:', error)
    this.$message.error(error.message || '付款失败，请稍后重试')
  } finally {
    this.paymentDialog.submitting = false
  }
},

    // ============================================================
    // 修改状态弹窗
    // ============================================================
    openStatusDialog() {
      if (this.selectedRows.length !== 1) {
        this.$message.warning('请选择一条记录')
        return
      }

      const row = this.selectedRows[0]
      this.statusDialog.rowData = row
      this.statusForm = {
        房屋地址: row.房屋地址 || '',
        状态: row.状态 || ''
      }
      this.statusDialog.visible = true
      this.$nextTick(() => {
        if (this.$refs.statusFormRef) {
          this.$refs.statusFormRef.clearValidate()
        }
      })
    },

    resetStatusForm() {
      this.statusDialog.submitting = false
      this.statusDialog.rowData = null
      if (this.$refs.statusFormRef) {
        this.$refs.statusFormRef.clearValidate()
      }
    },

    async handleStatusSubmit() {
      const row = this.statusDialog.rowData
      const newStatus = this.statusForm.状态
      const 未收 = row.未收 || 0

      let needConfirm = false
      let confirmMsg = ''

      if (未收 === 0 && newStatus === '待收') {
        needConfirm = true
        confirmMsg = '已完成全部收款，是否仍要改为待收？'
      } else if (未收 === 0 && newStatus === '部分收款') {
        needConfirm = true
        confirmMsg = '已完成全部收款，是否仍要改为部分收款？'
      } else if (未收 !== 0 && newStatus === '已完成') {
        needConfirm = true
        confirmMsg = '未完成全部收款，是否仍要改为已完成？'
      }

      if (needConfirm) {
        try {
          await this.$confirm(confirmMsg, '提示', {
            type: 'warning',
            confirmButtonText: '确认修改',
            cancelButtonText: '取消'
          })
        } catch {
          return
        }
      }

      if (row.状态 === newStatus) {
        this.$message.warning('状态未发生变化')
        return
      }

      this.statusDialog.submitting = true

      try {
        const params = {
          id: String(row.id),
          状态: newStatus
        }

        console.log('修改状态参数===', params)

        const res = await cfPlanApi.updatePlan(params)
        console.log('修改状态返回===', res)

        if (res.code === 200) {
          this.$message.success('状态修改成功！')
          this.statusDialog.visible = false
          this.loadData()
        } else {
          this.$message.error(res.message || '修改状态失败')
        }
      } catch (error) {
        console.error('修改状态失败:', error)
        this.$message.error(error.message || '修改状态失败，请稍后重试')
      } finally {
        this.statusDialog.submitting = false
      }
    },

    // ============================================================
    // 查询付款记录弹窗
    // ============================================================
    async openRecordDialog() {
      if (this.selectedRows.length !== 1) {
        this.$message.warning('请选择一条记录')
        return
      }

      const row = this.selectedRows[0]
      this.recordDialog.rowData = row
      this.recordDialog.visible = true
      this.recordDialog.loading = true
      this.recordDialog.recordList = []

      try {
        const recordRes = await cfPlanApi.selectDetail({
          付款ID: String(row.id)
        })
        console.log('查询付款记录返回:', recordRes)

        if (recordRes.code === 200) {
          const records = recordRes.data?.list || []
          this.recordDialog.recordList = records.map(item => {
            if (item.实际收款时间) {
              const str = String(item.实际收款时间)
              if (str.length === 10) {
                item.实际收款时间 = item.实际收款时间 * 1000
              }
            }
            return item
          })
        } else {
          this.$message.error(recordRes.message || '查询付款记录失败')
        }
      } catch (error) {
        console.error('查询付款记录失败:', error)
        this.$message.error('查询付款记录失败，请重试')
      } finally {
        this.recordDialog.loading = false
      }
    },

    resetRecordDialog() {
      this.recordDialog.recordList = []
      this.recordDialog.rowData = null
    },

    // ============================================================
    // 修改付款记录
    // ============================================================
    openEditRecordDialog(row) {
      this.editRecordDialog.rowData = row
      this.editRecordForm = {
        实际收款时间: row.实际收款时间 || null,
        实际收款金额: row.实际收款金额 || null,
        租金: row.租金 || 0,
        宽带费: row.宽带费 || 0,
        水电费: row.水电费 || 0,
        押金总额: row.押金总额 || 0,
        状态: row.状态 || '',
        备注: row.备注 || ''
      }
      this.editRecordDialog.visible = true
      this.$nextTick(() => {
        if (this.$refs.editRecordFormRef) {
          this.$refs.editRecordFormRef.clearValidate()
        }
      })
    },

    resetEditRecordForm() {
      this.editRecordDialog.submitting = false
      this.editRecordDialog.rowData = null
      if (this.$refs.editRecordFormRef) {
        this.$refs.editRecordFormRef.clearValidate()
      }
    },

    async handleEditRecordSubmit() {
      try {
        await this.$refs.editRecordFormRef.validate()
      } catch (error) {
        this.$message.warning('请完善表单信息')
        return
      }

      const row = this.editRecordDialog.rowData
      const form = this.editRecordForm

      this.editRecordDialog.submitting = true

      try {
        const params = {
          id: row.id,
          实际收款时间: Math.floor(form.实际收款时间 / 1000),
          实际收款金额: form.实际收款金额,
          租金: form.租金 || 0,
          宽带费: form.宽带费 || 0,
          水电费: form.水电费 || 0,
          押金总额: form.押金总额 || 0,
          状态: form.状态,
          备注: form.备注 || ''
        }

        console.log('修改付款记录参数===', params)

        const res = await cfPlanApi.updateDetail(params)
        console.log('修改付款记录返回===', res)

        if (res.code === 200) {
          this.$message.success('修改成功！')
          this.editRecordDialog.visible = false
          // 刷新记录列表
          await this.openRecordDialog()
          // 刷新主表格
          this.loadData()
        } else {
          this.$message.error(res.message || '修改失败')
        }
      } catch (error) {
        console.error('修改失败:', error)
        this.$message.error(error.message || '修改失败，请稍后重试')
      } finally {
        this.editRecordDialog.submitting = false
      }
    },

    // ============================================================
    // 删除付款记录
    // ============================================================
    async handleDeleteRecord(row) {
      this.$confirm(
        `确认删除该付款记录吗？删除后不可恢复！`,
        '删除确认',
        {
          type: 'warning',
          confirmButtonText: '确认删除',
          cancelButtonText: '取消'
        }
      ).then(async () => {
        try {
          row.deleting = true
          const res = await cfPlanApi.deleteDetail({ id: row.id })
          console.log('删除付款记录返回===', res)

          if (res.code === 200 && res.data?.deleted) {
            this.$message.success('删除成功！')
            // 刷新记录列表
            await this.openRecordDialog()
            // 刷新主表格
            this.loadData()
          } else {
            this.$message.warning(res.data?.message || '删除失败')
          }
        } catch (error) {
          console.error('删除失败:', error)
          this.$message.error(error.message || '删除失败，请稍后重试')
        } finally {
          row.deleting = false
        }
      }).catch(() => {})
    },

    // ====== 刷新数据 ======
    refresh() {
      this.loadData()
    }
  }
}
</script>

<style scoped>
/* ... 样式保持不变 ... */
.cf-plan-container {
  padding: 20px;
  background: #f0f2f5;
  min-height: 100vh;
}

.filter-section {
  background: #fff;
  padding: 20px 20px 0 20px;
  border-radius: 4px;
  margin-bottom: 16px;
}

.filter-form {
  margin-bottom: 0;
}

.button-section {
  background: #fff;
  padding: 12px 20px;
  border-radius: 4px;
  margin-bottom: 16px;
}

.button-section .el-button {
  margin-bottom: 4px;
}

.table-section {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
}

.pagination-section {
  margin-top: 16px;
  text-align: right;
  flex-shrink: 0;
}

.detail-dialog .el-tabs,
.record-dialog .el-tabs {
  margin-top: 0;
}

.detail-dialog .el-tabs__content {
  padding: 20px;
  max-height: 500px;
  overflow-y: auto;
}

.empty-tip {
  text-align: center;
  padding: 20px 0;
  color: #909399;
  font-size: 13px;
  background: #f5f7fa;
  border-radius: 4px;
  margin-top: 8px;
}

.preview-thumbnail {
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-placeholder {
  font-size: 12px;
  color: #909399;
}

.file-group {
  margin-top: 8px;
}

.file-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0 10px 0;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 10px;
}

.file-group-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.certificate-group {
  margin-bottom: 20px;
}

.certificate-group:last-child {
  margin-bottom: 0;
}

.certificate-group-title {
  font-size: 14px;
  font-weight: 600;
  color: #409EFF;
  padding: 8px 0 12px 0;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 10px;
}

.divider {
  display: flex;
  align-items: center;
  margin: 16px 0 12px 0;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #dcdfe6;
}

.divider-text {
  padding: 0 16px;
  font-size: 14px;
  color: #909399;
  font-weight: 500;
}

@media (max-width: 768px) {
  .cf-plan-container {
    padding: 10px;
  }

  .filter-form .el-form-item {
    display: block;
    margin-right: 0;
  }

  .button-section .el-button {
    margin-bottom: 8px;
  }
}
</style>